// =====================================================================
// LeavesTimelineView — Vue d'ensemble Gantt (Tab 5 in AdminConges)
// =====================================================================
// Gantt: sticky left user col + sticky header (month groups + day numbers)
// Bars: colored horizontal spans per leave, clickable view-only modal
// Modal: "Voir dans Historique" calls onNavigateToHistorique prop
// Permission gate: leaves.read.all (enforced by parent AdminConges)
// API: leavesApi.listAll({ status: 'APPROVED' }) + usersApi.list()
// =====================================================================

import { useState, useEffect, useMemo } from 'react';
import { leaves as leavesApi, users as usersApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { marqueurInactif } from '../../lib/tri-personnes.js';

// ─── Color map v2 ─────────────────────────────────────────────────────
const LEAVE_COLORS = {
  VA:       { bg: '#22c55e', fg: '#fff',    border: '#16a34a', label: 'Conges payes'          },
  'VA-0.5': { bg: '#bbf7d0', fg: '#14532d', border: '#4ade80', label: 'Conges payes (1/2j)',  stripe: true },
  VAN:      { bg: '#86efac', fg: '#14532d', border: '#4ade80', label: 'Vacances non payees'   },
  VAEN1:    { bg: '#67e8f9', fg: '#164e63', border: '#22d3ee', label: 'Vacances reportees'    },
  AA:       { bg: '#a78bfa', fg: '#fff',    border: '#8b5cf6', label: 'Absence autorisee'     },
  REC:      { bg: '#fbbf24', fg: '#78350f', border: '#f59e0b', label: 'Recuperation'          },
  MAT:      { bg: '#fbcfe8', fg: '#9d174d', border: '#f9a8d4', label: 'Maternite'             },
  PAT:      { bg: '#60a5fa', fg: '#fff',    border: '#3b82f6', label: 'Paternite'             },
  MAL:      { bg: '#f97316', fg: '#fff',    border: '#ea580c', label: 'Maladie'               },
};

const MONTH_FR = ['janvier','fevrier','mars','avril','mai','juin','juillet','aout','septembre','octobre','novembre','decembre'];
const DAY_SHORT = ['Di','Lu','Ma','Me','Je','Ve','Sa'];
const COL_USER_W = 220;

// ─── Pure helpers ──────────────────────────────────────────────────────
function todayYmd() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`;
}

function ymd(y, m, d) {
  return `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
}

function fmtDayFR(s) {
  if (!s) return '\u2014';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
}

function isWeekend(y, m, d) {
  const w = new Date(y, m, d).getDay();
  return w === 0 || w === 6;
}

function daysInMonth(y, m) {
  return new Date(y, m + 1, 0).getDate();
}

function workdays(start, end) {
  let n = 0;
  const d = new Date(start.replace(/-/g, '/'));
  const e = new Date(end.replace(/-/g, '/'));
  while (d <= e) {
    if (![0, 6].includes(d.getDay())) n++;
    d.setDate(d.getDate() + 1);
  }
  return n;
}

// 5 days prev month + current month + 5 days next month
function getRange(year, month) {
  const days = [];
  const pm = month === 0 ? 11 : month - 1;
  const py = month === 0 ? year - 1 : year;
  const pDays = daysInMonth(py, pm);
  for (let d = pDays - 4; d <= pDays; d++) days.push({ y: py, m: pm, d, cur: false });
  for (let d = 1; d <= daysInMonth(year, month); d++) days.push({ y: year, m: month, d, cur: true });
  const nm = month === 11 ? 0 : month + 1;
  const ny = month === 11 ? year + 1 : year;
  for (let d = 1; d <= 5; d++) days.push({ y: ny, m: nm, d, cur: false });
  return days;
}

function getInitials(name) {
  return (name || '?').split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
}

const AVATAR_COLORS = [
  '#6366f1','#8b5cf6','#ec4899','#f97316','#14b8a6','#06b6d4','#3b82f6','#84cc16',
  '#a16207','#e11d48','#7c3aed','#0ea5e9','#475569','#a855f7','#d946ef','#0891b2',
  '#dc2626','#9333ea','#10b981','#0284c7',
];

function avatarColor(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffff;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function stripeGradient(bg) {
  return `repeating-linear-gradient(-45deg, ${bg}, ${bg} 3px, rgba(255,255,255,.4) 3px, rgba(255,255,255,.4) 6px)`;
}

// ─── Shared style objects ──────────────────────────────────────────────
const btnNav = {
  padding: '5px 10px', borderRadius: 6, border: '1px solid #cbd5e1',
  background: 'white', fontSize: 15, lineHeight: 1, cursor: 'pointer',
  color: '#334155', fontFamily: 'inherit',
};

// ─── Component ────────────────────────────────────────────────────────
export default function LeavesTimelineView({ onNavigateToHistorique }) {
  const today = todayYmd();
  const now = new Date();
  const { can } = useAuth();
  const canApprove = can('leaves.approve.all');
  const canCancelApproved = can('leaves.cancel.approved');

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [allLeaves, setAllLeaves] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTypes, setActiveTypes] = useState(() => new Set(Object.keys(LEAVE_COLORS)));
  const [modalLeave, setModalLeave] = useState(null);
  const [modalUser, setModalUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  async function reloadLeaves() {
    setLoading(true);
    try {
      const all = await leavesApi.listAll({ limit: 1000 });
      // Day 9: also include CANCELLED so admin can restore
      setAllLeaves(Array.isArray(all) ? all : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }
  async function handleApprove() {
    if (!modalLeave) return;
    setActionLoading(true); setActionMsg(null);
    try {
      await leavesApi.approve(modalLeave.id);
      setActionMsg({ type: 'success', text: 'Congé approuvé ✓' });
      await reloadLeaves();
      setTimeout(() => { setModalLeave(null); setModalUser(null); setActionMsg(null); }, 800);
    } catch (e) {
      setActionMsg({ type: 'error', text: e?.response?.data?.message || e.message });
    } finally { setActionLoading(false); }
  }
  async function handleReject() {
    if (!modalLeave) return;
    const reason = window.prompt('Motif du refus :', '');
    if (!reason) return;
    setActionLoading(true); setActionMsg(null);
    try {
      await leavesApi.reject(modalLeave.id, reason);
      setActionMsg({ type: 'success', text: 'Congé refusé' });
      await reloadLeaves();
      setTimeout(() => { setModalLeave(null); setModalUser(null); setActionMsg(null); }, 800);
    } catch (e) {
      setActionMsg({ type: 'error', text: e?.response?.data?.message || e.message });
    } finally { setActionLoading(false); }
  }
  async function handleRestore() {
    if (!modalLeave) return;
    if (!window.confirm('Rétablir ce congé annulé ? (réapplique balance + attendances)')) return;
    setActionLoading(true); setActionMsg(null);
    try {
      await leavesApi.restore(modalLeave.id);
      setActionMsg({ type: 'success', text: 'Congé rétabli ✓' });
      await reloadLeaves();
      setTimeout(() => { setModalLeave(null); setModalUser(null); setActionMsg(null); }, 800);
    } catch (e) {
      setActionMsg({ type: 'error', text: e?.response?.data?.message || e.message });
    } finally { setActionLoading(false); }
  }
  // Day 11 — annuler un congé APPROUVÉ (cascade) directement depuis la Vue d'ensemble
  async function handleCancel() {
    if (!modalLeave) return;
    if (!window.confirm('Annuler ce congé approuvé ? Cela supprime Attendance + TS et restitue le solde.')) return;
    setActionLoading(true); setActionMsg(null);
    try {
      await leavesApi.cancelApproved(modalLeave.id);
      setActionMsg({ type: 'success', text: 'Congé annulé ✓' });
      await reloadLeaves();
      setTimeout(() => { setModalLeave(null); setModalUser(null); setActionMsg(null); }, 800);
    } catch (e) {
      setActionMsg({ type: 'error', text: e?.response?.data?.message || e.message });
    } finally { setActionLoading(false); }
  }

  useEffect(() => {
    Promise.all([
      // Day 9: fetch all statuses (PENDING + APPROVED + REJECTED + CANCELLED for restore)
      leavesApi.listAll({ limit: 1000 }).then((all) => all || []),
      // Comptes INACTIFS compris : leurs congés approuvés restent dans la
      // période affichée. (L'appel portait `isActive: true` — une clé que le
      // serveur ne connaît pas et jette en silence : il n'a jamais filtré.)
      usersApi.list(),
    ]).then(([leaveData, userData]) => {
      setAllLeaves(Array.isArray(leaveData) ? leaveData : []);
      const users = Array.isArray(userData) ? userData : (userData.items || userData.users || []);
      setAllUsers(users);
    }).catch(err => {
      setError(err.response?.data?.message || err.message || 'Erreur de chargement');
    }).finally(() => setLoading(false));
  }, []);

  // ESC closes modal
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const days = useMemo(() => getRange(year, month), [year, month]);

  const monthGroups = useMemo(() => {
    const groups = [], seen = new Set();
    days.forEach((day) => {
      const k = `${day.y}-${day.m}`;
      if (!seen.has(k)) { seen.add(k); groups.push({ y: day.y, m: day.m, count: 0 }); }
      groups[groups.length - 1].count++;
    });
    return groups;
  }, [days]);

  const totals = useMemo(() => {
    const t = new Array(days.length).fill(0);
    for (const leave of allLeaves) {
      if (!activeTypes.has(leave.type)) continue;
      for (let i = 0; i < days.length; i++) {
        const d = ymd(days[i].y, days[i].m, days[i].d);
        if (d >= leave.startDate && d <= leave.endDate) t[i]++;
      }
    }
    return t;
  }, [allLeaves, days, activeTypes]);

  function prevMonth() {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  }
  function goToday() {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth());
  }
  function toggleType(type) {
    setActiveTypes(prev => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  }
  function openModal(leave, user) {
    setModalLeave(leave);
    setModalUser(user);
  }
  function closeModal() {
    setModalLeave(null);
    setModalUser(null);
  }
  function gotoHistorique() {
    closeModal();
    if (onNavigateToHistorique) onNavigateToHistorique();
  }

  // Index construit UNE fois par (congés, types cochés, mois) : la liste des
  // lignes et le dessin des barres le lisent tous les deux. Sans mémoire, le
  // même balayage (congés × jours) repartait à chaque redessin, deux fois.
  const barresParPersonne = useMemo(() => {
    const index = new Map();
    for (const leave of allLeaves) {
      if (!activeTypes.has(leave.type)) continue;
      let si = -1, ei = -1;
      for (let i = 0; i < days.length; i++) {
        const d = ymd(days[i].y, days[i].m, days[i].d);
        if (d >= leave.startDate && si === -1) si = i;
        if (d <= leave.endDate) ei = i;
      }
      if (si === -1 || ei < si) continue;
      if (!index.has(leave.userId)) index.set(leave.userId, []);
      index.get(leave.userId).push({ leave, si, span: ei - si + 1 });
    }
    return index;
  }, [allLeaves, activeTypes, days]);

  // Qui a QUELQUE CHOSE dans le mois, tous types confondus. Séparé de l'index
  // ci-dessus : si la présence d'une ligne dépendait des cases cochées, un
  // ancien collaborateur disparaîtrait de la liste en décochant son type.
  const personnesAvecConge = useMemo(() => {
    const premier = ymd(days[0]?.y, days[0]?.m, days[0]?.d);
    const dernier = ymd(days[days.length - 1]?.y, days[days.length - 1]?.m, days[days.length - 1]?.d);
    const vus = new Set();
    for (const leave of allLeaves) {
      if (leave.endDate >= premier && leave.startDate <= dernier) vus.add(leave.userId);
    }
    return vus;
  }, [allLeaves, days]);

  function barsForUser(userId) {
    return barresParPersonne.get(userId) || [];
  }

  // Day 11++ (01-06-2026) — Track packing pentru bars suprapuse.
  // Algoritm greedy: pentru fiecare bar (sortat după si crescător), îl pun
  // în primul track unde nu se suprapune cu nimic existent. Returnează
  // array de tracks (fiecare track = array de bars non-overlapping).
  // Fix issue Pierre: PENDING 01/06→17/06 era ascuns de APPROVED single-day.
  function packBarsIntoTracks(bars) {
    const sorted = [...bars].sort((a, b) => a.si - b.si || (b.span - a.span));
    const tracks = [];
    for (const bar of sorted) {
      let placed = false;
      for (const track of tracks) {
        const last = track[track.length - 1];
        const lastEnd = last.si + last.span - 1;
        if (bar.si > lastEnd) { track.push(bar); placed = true; break; }
      }
      if (!placed) tracks.push([bar]);
    }
    return tracks;
  }

  // Lookup user name from id (for popup "Décidé par" rows). Falls back to id-snippet.
  function userNameById(id) {
    if (!id) return null;
    const u = allUsers.find(x => x.id === id);
    return u ? u.name : id;
  }

  // Format datetime FR for popup (decidedAt comes as ISO string)
  function fmtDateTimeFR(iso) {
    if (!iso) return '—';
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return '—';
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yy = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, '0');
    const mi = String(dt.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  }

  if (loading) return <div style={{ padding: 32, color: '#64748b' }}>Chargement\u2026</div>;
  if (error) return (
    <div style={{ padding: 16, background: '#FEE2E2', color: '#991B1B', borderRadius: 6, border: '1px solid #FCA5A5' }}>
      Erreur de chargement: {error}
    </div>
  );

  return (
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap', background: '#fafafa' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* View toggle — Trimestre reserved for future */}
          <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: 6, overflow: 'hidden', background: 'white' }}>
            <button style={{ padding: '5px 13px', border: 'none', background: '#eff6ff', color: '#1d4ed8', fontSize: 13, fontWeight: 600, cursor: 'default', fontFamily: 'inherit', borderRight: '1px solid #e2e8f0' }}>
              Mois
            </button>
            <button disabled style={{ padding: '5px 13px', border: 'none', background: 'transparent', fontSize: 13, cursor: 'not-allowed', color: '#cbd5e1', fontFamily: 'inherit' }}>
              Trimestre
            </button>
          </div>
          <button onClick={prevMonth} style={btnNav}>&lsaquo;</button>
          <span style={{ fontSize: 14, fontWeight: 600, minWidth: 120, textAlign: 'center', color: '#1e293b' }}>
            {MONTH_FR[month]} {year}
          </span>
          <button onClick={nextMonth} style={btnNav}>&rsaquo;</button>
          <button onClick={goToday} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#2563eb', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Aujourd&apos;hui
          </button>
        </div>

        {/* Filter chips */}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginLeft: 'auto' }}>
          {Object.entries(LEAVE_COLORS).map(([type, col]) => (
            <div
              key={type}
              onClick={() => toggleType(type)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 9px', borderRadius: 20, fontSize: 11.5, fontWeight: 500,
                cursor: 'pointer', userSelect: 'none',
                background: `${col.bg}18`, border: `1px solid ${col.border}55`, color: col.border,
                opacity: activeTypes.has(type) ? 1 : 0.3,
                transition: 'opacity .15s',
              }}
            >
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: col.bg, border: `1px solid ${col.border}`, flexShrink: 0 }} />
              {type}
            </div>
          ))}
        </div>
      </div>

      {/* ── Gantt table ── */}
      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: 'max-content' }}>
          <thead>
            {/* Row 1: month group labels */}
            <tr>
              <th style={{
                width: COL_USER_W, minWidth: COL_USER_W,
                position: 'sticky', left: 0, top: 0, zIndex: 30,
                background: '#f8fafc', borderRight: '2px solid #e2e8f0', borderBottom: '1px solid #e2e8f0',
                fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.5px',
                fontWeight: 500, padding: '7px 12px', textAlign: 'left',
              }}>
                Conges
              </th>
              {monthGroups.map(g => (
                <th key={`${g.y}-${g.m}`} colSpan={g.count} style={{
                  background: '#f8fafc', fontSize: 11.5, fontWeight: 700, color: '#475569',
                  textAlign: 'left', padding: '7px 10px',
                  borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0',
                  letterSpacing: '.2px', whiteSpace: 'nowrap',
                  position: 'sticky', top: 0, zIndex: 20,
                }}>
                  {MONTH_FR[g.m]} {g.y}
                </th>
              ))}
            </tr>
            {/* Row 2: day numbers */}
            <tr>
              <th style={{
                width: COL_USER_W, minWidth: COL_USER_W,
                position: 'sticky', left: 0, top: 31, zIndex: 30,
                background: '#f8fafc', borderRight: '2px solid #e2e8f0', borderBottom: '2px solid #e2e8f0',
                fontSize: 11, color: '#94a3b8', textTransform: 'uppercase',
                fontWeight: 500, padding: '4px 12px', textAlign: 'left',
              }}>
                Employe
              </th>
              {days.map((day, i) => {
                const d = ymd(day.y, day.m, day.d);
                const isToday = d === today;
                const isWknd = isWeekend(day.y, day.m, day.d);
                const isOther = !day.cur;
                const dw = new Date(day.y, day.m, day.d).getDay();
                return (
                  <th key={i} style={{
                    width: 30, minWidth: 30,
                    background: isToday ? '#fef9c3' : (isWknd || isOther) ? '#fafafa' : '#f8fafc',
                    fontSize: 11, fontWeight: isToday ? 700 : 500,
                    color: isToday ? '#854d0e' : isWknd ? '#cbd5e1' : isOther ? '#e2e8f0' : '#64748b',
                    textAlign: 'center', padding: '4px 0 5px',
                    borderBottom: `2px solid ${isToday ? '#f59e0b' : '#e2e8f0'}`,
                    borderRight: '1px solid #f1f5f9',
                    position: 'sticky', top: 31, zIndex: 20,
                  }}>
                    <div style={{ fontSize: 11 }}>{day.d}</div>
                    <div style={{ fontSize: 9, marginTop: 1, opacity: .7 }}>{DAY_SHORT[dw]}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {allUsers.filter(u => u.isActive !== false || personnesAvecConge.has(u.id)).map(user => {
              const bars = barsForUser(user.id);
              // Day 11++ (01-06-2026) \u2014 packed tracks per user pentru bars suprapuse.
              // O entitate = N tracks. Fiecare track = un row vizual. Min 1 row chiar dac\u0103 0 bars.
              const tracks = bars.length === 0 ? [[]] : packBarsIntoTracks(bars);

              const initials = getInitials(user.name || user.username);
              const aColor = avatarColor(user.id);

              return tracks.map((trackBars, trackIdx) => {
                const isFirstTrack = trackIdx === 0;
                const isLastTrack = trackIdx === tracks.length - 1;
                const barsByStart = {};
                trackBars.forEach(b => {
                  if (!barsByStart[b.si]) barsByStart[b.si] = [];
                  barsByStart[b.si].push(b);
                });

                // Build cell list, skipping cells covered by colSpan
                const cells = [];
                const skip = new Set();
                days.forEach((day, i) => {
                  if (skip.has(i)) return;
                  const d = ymd(day.y, day.m, day.d);
                  const bar = barsByStart[i] ? barsByStart[i][0] : null;
                  const span = bar ? bar.span : 1;
                  if (bar) for (let s = i + 1; s < i + span; s++) skip.add(s);
                  cells.push({
                    day, i, bar, span,
                    isToday: d === today,
                    isWknd: isWeekend(day.y, day.m, day.d),
                    isOther: !day.cur,
                  });
                });

                return (
                  <tr
                    key={`${user.id}-t${trackIdx}`}
                    style={{ borderBottom: isLastTrack ? '1px solid #f1f5f9' : 'none' }}
                  >
                    {/* Sticky user cell \u2014 afi\u0219eaz\u0103 avatarul + numele doar pe primul track */}
                    <td style={{
                      width: COL_USER_W, minWidth: COL_USER_W,
                      padding: '0 12px', height: 36,
                      position: 'sticky', left: 0, background: 'white', zIndex: 5,
                      borderRight: '2px solid #e2e8f0', whiteSpace: 'nowrap',
                    }}>
                      {isFirstTrack && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: '50%', background: aColor, color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 9.5, fontWeight: 700, flexShrink: 0, letterSpacing: '-.3px',
                          }}>
                            {initials}
                          </div>
                          <span style={{ fontSize: 13, color: '#334155' }}>{user.name}{marqueurInactif(user)}</span>
                          {tracks.length > 1 && (
                            <span title={`${tracks.length} pistes (chevauchements)`} style={{
                              fontSize: 9.5, color: '#64748b', background: '#f1f5f9',
                              padding: '1px 5px', borderRadius: 8, marginLeft: 'auto', flexShrink: 0,
                            }}>{tracks.length}\u00d7</span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Day cells */}
                    {cells.map(({ i, isToday, isWknd, isOther, bar, span }) => {
                      const c = bar ? (LEAVE_COLORS[bar.leave.type] || LEAVE_COLORS.VA) : null;
                      return (
                        <td key={i} colSpan={span} style={{
                          minWidth: 30, height: 36,
                          background: isToday ? '#fefce8' : isWknd ? '#f9fafb' : isOther ? '#fafafa' : 'white',
                          borderRight: '1px solid #f1f5f9',
                          position: 'relative', verticalAlign: 'top',
                        }}>
                          {bar && c && (() => {
                            const status = bar.leave.status;
                            const isPending = status === 'PENDING';
                            const isRejected = status === 'REJECTED';
                            const isCancelled = status === 'CANCELLED';
                            const statusLabel = isPending ? ' (en attente)' : isRejected ? ' (refus\u00e9e)' : isCancelled ? ' (annul\u00e9e)' : '';
                            const bgStyle = isPending
                              ? stripeGradient(c.bg)
                              : (c.stripe ? stripeGradient(c.bg) : c.bg);
                            const lineColor = isRejected ? '#dc2626' : isCancelled ? '#64748b' : null;
                            return (
                              <div
                                onClick={() => openModal(bar.leave, user)}
                                title={`${user.name}\n${c.label}${statusLabel}\n${fmtDayFR(bar.leave.startDate)} \u2192 ${fmtDayFR(bar.leave.endDate)}`}
                                style={{
                                  position: 'absolute', top: 5, left: 2, right: 2, height: 26,
                                  borderRadius: 4, display: 'flex', alignItems: 'center',
                                  padding: '0 7px', fontSize: 11, fontWeight: 600,
                                  whiteSpace: 'nowrap', overflow: 'hidden', cursor: 'pointer',
                                  background: bgStyle,
                                  color: c.fg,
                                  borderLeft: `3px solid ${c.border}`,
                                  opacity: isRejected ? 0.55 : isCancelled ? 0.35 : 1,
                                  boxShadow: '0 1px 2px rgba(0,0,0,.1)',
                                  textDecoration: (isRejected || isCancelled) ? 'line-through' : 'none',
                                }}
                              >
                                {lineColor && (
                                  <span style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    background: `linear-gradient(to top right, transparent calc(50% - 1.5px), ${lineColor} calc(50% - 1px), ${lineColor} calc(50% + 1px), transparent calc(50% + 1.5px))`,
                                    pointerEvents: 'none',
                                  }} />
                                )}
                                <span style={{ position: 'relative', zIndex: 1 }}>
                                  {span >= 4 ? (span >= 7 ? `${(user.name || '').split(' ')[0]} \u2014 ${c.label}${statusLabel}` : `${c.label}${statusLabel}`) : null}
                                </span>
                              </div>
                            );
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                );
              });
            })}

            {/* Total row */}
            <tr style={{ borderTop: '2px solid #e2e8f0' }}>
              <td style={{
                width: COL_USER_W, minWidth: COL_USER_W,
                position: 'sticky', left: 0, background: '#f8fafc', zIndex: 5,
                fontSize: 11, fontWeight: 700, color: '#64748b',
                textTransform: 'uppercase', letterSpacing: '.5px',
                padding: '0 12px', height: 36, borderRight: '2px solid #e2e8f0',
              }}>
                Total
              </td>
              {days.map((day, i) => {
                const d = ymd(day.y, day.m, day.d);
                const n = totals[i];
                const heatBg = n >= 6 ? '#fef3c7' : n >= 4 ? '#f1f5f9' : 'transparent';
                const heatFg = n >= 6 ? '#92400e' : n >= 4 ? '#475569' : '#94a3b8';
                return (
                  <td key={i} style={{
                    background: d === today ? '#fefce8' : '#f8fafc',
                    textAlign: 'center', verticalAlign: 'middle',
                    height: 36, borderRight: '1px solid #f1f5f9',
                  }}>
                    {n > 0 && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 20, height: 20, borderRadius: 4,
                        fontSize: 10.5, fontWeight: 700, background: heatBg, color: heatFg,
                      }}>
                        {n}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '10px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        {Object.entries(LEAVE_COLORS).map(([type, col]) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: '#475569' }}>
            <div style={{
              width: 16, height: 12, borderRadius: 3, flexShrink: 0,
              background: col.stripe ? stripeGradient(col.bg) : col.bg,
              border: `1.5px solid ${col.border}`,
            }} />
            <span>{col.label}</span>
          </div>
        ))}
        {/* Day 9: status legend */}
        <div style={{ borderLeft: '1px solid #cbd5e1', height: 16, alignSelf: 'center', marginLeft: 4 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: '#475569' }}>
          <div style={{ width: 16, height: 12, borderRadius: 3, flexShrink: 0, background: '#bbf7d0', border: '1.5px solid #4ade80' }} />
          <span>Approuvée</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: '#475569' }}>
          <div style={{ width: 16, height: 12, borderRadius: 3, flexShrink: 0, background: stripeGradient('#bbf7d0'), border: '1.5px solid #4ade80' }} />
          <span>En attente</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: '#475569' }}>
          <div style={{
            width: 16, height: 12, borderRadius: 3, flexShrink: 0,
            background: '#bbf7d0', border: '1.5px solid #4ade80', opacity: 0.55,
            position: 'relative', overflow: 'hidden',
          }}>
            <span style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'linear-gradient(to top right, transparent calc(50% - 1px), #dc2626 calc(50% - 0.5px), #dc2626 calc(50% + 0.5px), transparent calc(50% + 1px))',
            }} />
          </div>
          <span>Refusée</span>
        </div>
      </div>

      {/* ── Modal (view-only) ── */}
      {modalLeave && modalUser && (() => {
        const c = LEAVE_COLORS[modalLeave.type] || LEAVE_COLORS.VA;
        // BUG-009 fix: foloseste workdaysCount din backend (deja exclude weekend + holidays)
        // Fallback la calc local (doar weekend) daca campul lipseste
        const wDays = modalLeave.workdaysCount ?? workdays(modalLeave.startDate, modalLeave.endDate);
        // Day 11++ (01-06-2026) \u2014 popup rows dinamice dup\u0103 status, cu decider lookup.
        // CANCELLED: arat\u0103 Annul\u00e9 par / Annul\u00e9 le / Motif annulation (cu fallback dac\u0103 fields lipsesc \u2014 Aurelien legacy)
        // REJECTED: Refus\u00e9 par / Refus\u00e9 le / Motif refus
        // APPROVED: Approuv\u00e9 par / Approuv\u00e9 le
        // PENDING: doar tipul + interval + motif user
        const status = modalLeave.status;
        const deciderName = userNameById(modalLeave.decidedByUserId);
        const decidedAtStr = modalLeave.decidedAt ? fmtDateTimeFR(modalLeave.decidedAt) : null;
        const rows = [
          { label: 'Type', value: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${c.bg}33`, color: c.border, border: `1px solid ${c.border}55` }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.bg, border: `1px solid ${c.border}`, display: 'inline-block' }} />
              {c.label}
            </span>
          )},
          { label: 'Debut', value: fmtDayFR(modalLeave.startDate) },
          { label: 'Fin', value: fmtDayFR(modalLeave.endDate) },
          // Day 11++ (01-06-2026) — AA/REC en HEURES: afișează durata în ore (partialDurationMin),
          // nu "1 jour ouvrable" (bug semnalat: cerere Miguel AA 2h00 apărea ca "1 jour ouvrable").
          { label: 'Duree', value: modalLeave.partialDurationMin
              ? `${Math.floor(modalLeave.partialDurationMin / 60)}h${String(modalLeave.partialDurationMin % 60).padStart(2, '0')} (heures)`
              : `${wDays} jour${wDays > 1 ? 's' : ''} ouvrable${wDays > 1 ? 's' : ''}` },
          { label: 'Motif (demande)', value: modalLeave.reason || '\u2014' },
        ];
        if (status === 'APPROVED') {
          rows.push({ label: 'Approuv\u00e9 par', value: deciderName || '\u2014' });
          if (decidedAtStr) rows.push({ label: 'Approuv\u00e9 le', value: decidedAtStr });
        } else if (status === 'REJECTED') {
          rows.push({ label: 'Refus\u00e9 par', value: deciderName || '\u2014' });
          if (decidedAtStr) rows.push({ label: 'Refus\u00e9 le', value: decidedAtStr });
          rows.push({ label: 'Motif refus', value: modalLeave.decidedReason || '\u2014' });
        } else if (status === 'CANCELLED') {
          rows.push({ label: 'Annul\u00e9 par', value: deciderName || (
            <span style={{ color: '#94a3b8', fontStyle: 'italic', fontWeight: 400 }}>\u2014  (donn\u00e9e historique)</span>
          )});
          rows.push({ label: 'Annul\u00e9 le', value: decidedAtStr || (
            <span style={{ color: '#94a3b8', fontStyle: 'italic', fontWeight: 400 }}>\u2014</span>
          )});
          rows.push({ label: 'Motif annulation', value: modalLeave.decidedReason || (
            <span style={{ color: '#94a3b8', fontStyle: 'italic', fontWeight: 400 }}>\u2014</span>
          )});
        }
        return (
          <div
            onClick={e => { if (e.target === e.currentTarget) closeModal(); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div style={{ background: 'white', borderRadius: 12, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,.22)', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{modalUser.name}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    {/* Day 11++ (29-05-2026) \u2014 show actual status, not hardcoded "approuv\u00e9" */}
                    {modalLeave.status === 'APPROVED'  && 'Cong\u00e9 approuv\u00e9'}
                    {modalLeave.status === 'PENDING'   && 'Demande en attente'}
                    {modalLeave.status === 'CANCELLED' && 'Cong\u00e9 annul\u00e9'}
                    {modalLeave.status === 'REJECTED'  && 'Cong\u00e9 refus\u00e9'}
                    {' \u2014 cliquez \u201cVoir dans Historique\u201d pour g\u00e9rer'}
                  </div>
                </div>
                <button onClick={closeModal} style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: '#f1f5f9', cursor: 'pointer', fontSize: 14, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  &#x2715;
                </button>
              </div>
              {/* Body */}
              <div style={{ padding: '16px 20px' }}>
                {rows.map((row, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: idx < rows.length - 1 ? '1px solid #f8fafc' : 'none', fontSize: 13, gap: 12 }}>
                    <span style={{ color: '#64748b', fontWeight: 500, flexShrink: 0 }}>{row.label}</span>
                    <span style={{ color: '#1e293b', fontWeight: 600, textAlign: 'right' }}>{row.value}</span>
                  </div>
                ))}
              </div>
              {/* Day 9 feedback */}
              {actionMsg && (
                <div style={{
                  padding: '8px 20px', fontSize: 12,
                  background: actionMsg.type === 'success' ? '#dcfce7' : '#fee2e2',
                  color: actionMsg.type === 'success' ? '#166534' : '#991b1b',
                  borderTop: '1px solid #e2e8f0',
                }}>{actionMsg.text}</div>
              )}
              {/* Footer */}
              <div style={{ padding: '14px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={closeModal} disabled={actionLoading} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #cbd5e1', background: 'white', fontSize: 13, cursor: actionLoading ? 'wait' : 'pointer', color: '#475569', fontFamily: 'inherit' }}>
                  Fermer
                </button>
                {/* Day 9: action buttons per status */}
                {modalLeave.status === 'PENDING' && canApprove && (
                  <>
                    <button onClick={handleReject} disabled={actionLoading} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #fca5a5', background: 'white', color: '#991b1b', fontSize: 13, fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}>
                      ✗ Refuser
                    </button>
                    <button onClick={handleApprove} disabled={actionLoading} style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#16a34a', color: 'white', fontSize: 13, fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}>
                      ✓ Approuver
                    </button>
                  </>
                )}
                {modalLeave.status === 'APPROVED' && canCancelApproved && (
                  <button onClick={handleCancel} disabled={actionLoading} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #fca5a5', background: 'white', color: '#991b1b', fontSize: 13, fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}>
                    ✗ Annuler
                  </button>
                )}
                {modalLeave.status === 'CANCELLED' && canCancelApproved && (
                  <button onClick={handleRestore} disabled={actionLoading} style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#4f46e5', color: 'white', fontSize: 13, fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}>
                    ↻ Rétablir
                  </button>
                )}
                <button onClick={gotoHistorique} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#2563eb', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>Historique</span>
                  <span>&#x2192;</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
